/**
 * Reveal
 *
 * Motion whileInView reveal wrapper for www-sbi (#7781 Motion standard S4, R-V6).
 * Fades and lifts content into view once, when it enters the viewport. Honors
 * prefers-reduced-motion: the content renders statically, no transform or fade.
 *
 * Usage:
 *   import { Reveal } from '../components/Reveal';
 *
 *   <Reveal>
 *     <h2>Section heading</h2>
 *   </Reveal>
 */

import * as React from "react";
import { motion, useReducedMotion } from "motion/react";

export interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Delay in seconds before the reveal starts (staggered entrances). */
  delay?: number;
  /** Initial offset along Y in px (default 24; 0 fades in place). */
  y?: number;
}

export function Reveal({ children, className, delay = 0, y = 24 }: RevealProps) {
  const prefersReduced = useReducedMotion() ?? false;
  if (prefersReduced) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-64px" }}
      transition={{ duration: 0.6, delay, ease: [0, 0, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default Reveal;
